import { v4 as uuid } from 'uuid';

import { createContextProvider } from './internal/context-provider';
import { createCoreEvents } from './internal/core-events/core-events';
import { createIdentifiersProvider } from './internal/identifiers-provider';
import { createNetwork } from './internal/network';
import { createPersistence } from './internal/persistence';
import { createScheduler } from './internal/scheduler';
import { type PartialPicoXConfiguration, buildPicoXConfiguration } from './models/configuration';
import { type JsonObject, type StoredEvent } from './models/event';

/**
 * Per-event additional identifiers that override or supplement the source-level identifiers.
 * Keys map to well-known PicoX identifier fields.
 */
export type TrackAdditionalIdentifiers = {
  user_id?: string | null;
};

/**
 * PicoX is the main entry point for the PicoX analytics SDK.
 *
 * It creates all the necessary components and starts the scheduling of sync operations.
 */
export type PicoX = {
  /**
   * Tracks an event with the given name and payload
   * @param eventName Name of the event to track
   * @param payload Payload data associated with the event
   * @param additionalIdentifiers Per-event identifiers that override source-level ones
   */
  track: (
    eventName: string,
    payload: JsonObject,
    additionalIdentifiers?: TrackAdditionalIdentifiers,
  ) => Promise<void>;
};

export const createPicoX = async (options: PartialPicoXConfiguration): Promise<PicoX> => {
  const configuration = buildPicoXConfiguration(options);

  const persistence = await createPersistence(configuration);
  const network = createNetwork(configuration);
  const scheduler = createScheduler(persistence, network, configuration);
  const identifiersProvider = createIdentifiersProvider(
    configuration.identifiersSuffix,
    configuration.cookieDomain,
  );
  const contextProvider = createContextProvider(configuration);

  const track = async (
    eventName: string,
    payload: JsonObject,
    additionalIdentifiers?: TrackAdditionalIdentifiers,
  ) => {
    const currentDate = new Date();

    const identifiers = identifiersProvider.currentIdentifiers();
    const customIdentifiers = configuration.additionalIdentifiers();
    const allIdentifiers = {
      ...identifiers,
      ...customIdentifiers,
      ...(additionalIdentifiers ?? {}),
    };

    const context = contextProvider.currentContext(currentDate);
    const customContext = configuration.additionalContext();
    const allContext = {
      ...context,
      ...customContext,
    };

    const event: StoredEvent = {
      event_id: uuid(),
      event_name: eventName,
      payload,
      identifiers: allIdentifiers,
      context: allContext,
      event_timestamp: currentDate,
    };

    // Apply event processors in sequence
    let processedEvent: StoredEvent | undefined = event;
    for (const processor of configuration.eventProcessors) {
      processedEvent = processor.processEvent(processedEvent);

      // The event can be dropped if filtered out by the processor
      if (!processedEvent) {
        return;
      }
    }
    await persistence.storeEvent(processedEvent);
  };

  const coreEvents = createCoreEvents(track);

  // Start the scheduling of sync operations
  scheduler.startScheduling();
  // Start the core events
  coreEvents.start();

  return {
    track,
  };
};
